import type { CSSProperties } from 'react';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import { SparkIcon } from '../SparkIcon/SparkIcon';
import { sparkAssetUrl } from '../../utils/sparkAssetUrl';
import './TaskTypeIcon.css';

export type TaskTypeIconType =
  | 'classification'
  | 'classification-multilabel'
  | 'extraction'
  | 'summarization'
  | 'content-generation'
  | 'unknown';

/** Hover-tooltip label per tile — matches the app's task-type naming. */
export const TASK_TYPE_ICON_LABELS: Record<TaskTypeIconType, string> = {
  classification: 'Single label classification',
  'classification-multilabel': 'Multi label classification',
  extraction: 'Entity Extraction',
  summarization: 'Summarization',
  'content-generation': 'Content Generation',
  unknown: 'Task type not set',
};

/**
 * Colored task-type tile (gradient background + glyph) used on project and
 * task cards, with a hover tooltip naming the task type. Assets live in
 * `public/task-type-icons/`. Figma: Projects task-type icons (`8001:3820`
 * single-label, `8006:4497` multi-label, `8001:2696` extraction,
 * `8001:3833` content generation, `8001:2702` summarization, `8001:3827`
 * unknown).
 *
 * Agentic tasks (`agentic`) get the Figma robot badge (`8140:1608`): a
 * green circle half the tile size (exact Figma #D8F5E5 fill via the
 * `--spark-task-type-icon-badge-bg` component property — no Spark token
 * matches it), 2px `--semantic-surface-white` border and a
 * `--semantic-feedback-success-on-surface` (#32815A) robot glyph, overflowing
 * the tile's top-right corner. The badge scales with `size` and carries its
 * own "Agentic task" hover tooltip, separate from the tile's task-type one.
 */
export type TaskTypeIconProps = {
  type?: TaskTypeIconType;
  /** Tile edge in px. Figma uses 40 on task cards and 32 on project-summary rows. */
  size?: number;
  /** Show the robot badge on the tile's top-right corner (agentic tasks). */
  agentic?: boolean;
  /** Tooltip text override; defaults to the task-type label for `type`. */
  tooltip?: string;
  /** Set false to render the bare tile without a tooltip. */
  showTooltip?: boolean;
  className?: string;
};

/** Robot glyph edge relative to the tile: 12px on the 32px Figma tile. */
const BADGE_GLYPH_RATIO = 12 / 32;

/** Hover text of the robot badge on agentic tiles. */
export const TASK_TYPE_ICON_AGENTIC_LABEL = 'Agentic task';

export function TaskTypeIcon({
  type = 'unknown',
  size = 40,
  agentic = false,
  tooltip,
  showTooltip = true,
  className = '',
}: TaskTypeIconProps) {
  const tile = (
    <img
      className={['spark-task-type-icon', !agentic && className].filter(Boolean).join(' ')}
      src={sparkAssetUrl(`task-type-icons/${type}.png`)}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
    />
  );

  const tileWithTooltip = showTooltip ? (
    <TooltipTrigger text={tooltip ?? TASK_TYPE_ICON_LABELS[type]} placement="top">
      {tile}
    </TooltipTrigger>
  ) : (
    tile
  );

  if (!agentic) return tileWithTooltip;

  const badge = (
    <span className="spark-task-type-icon-badge" aria-hidden="true">
      <SparkIcon slug="robot" size={Math.round(size * BADGE_GLYPH_RATIO)} />
    </span>
  );

  // The badge is a sibling of the tile's trigger (not nested in it) so hovering
  // the robot shows only "Agentic task", never both tooltips at once.
  return (
    <span
      className={['spark-task-type-icon-wrap', className].filter(Boolean).join(' ')}
      style={{ '--spark-task-type-icon-size': `${size}px` } as CSSProperties}
      data-testid="spark-task-type-icon-agentic"
    >
      {tileWithTooltip}
      {showTooltip ? (
        <TooltipTrigger
          text={TASK_TYPE_ICON_AGENTIC_LABEL}
          placement="top"
          className="spark-task-type-icon-badge-anchor"
        >
          {badge}
        </TooltipTrigger>
      ) : (
        <span className="spark-task-type-icon-badge-anchor">{badge}</span>
      )}
    </span>
  );
}
